import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  LinearProgress,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SearchComponent from './SearchComponent';
import { searchProjects } from '../../services/searchService';

const UnifiedSearchPage = ({ 
  title, 
  columns = [],
  onRowClick = null,
  getRowLink = null,
  loadDataFunction = null
}) => {
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadInitialData = useCallback(async () => {
    if (!loadDataFunction) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const searchCriteria = { page: 1, limit: 10000 }; // Load all records for client-side pagination
      const response = await loadDataFunction(searchCriteria);
      const results = Array.isArray(response) ? response : (response?.results || response?.data || []);
      const formattedData = results.map((item, index) => ({
        id: item.id || item.projectCode || item.project_code || index,
        projectCode: item.projectCode || item.project_code,
        budgetCode: item.budgetCode || item.budget_code,
        requestorName: item.requestorName || item.requestor_name,
        personInCharge: item.personInCharge || item.person_in_charge,
        modelName: item.modelName || item.model_name,
        projectStatus: item.projectStatus || item.project_status,
        backgroundColor: item.backgroundColor
      }));
      setFilteredRows(formattedData);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data';
      setError(`Error: ${errorMessage}`);
      setFilteredRows([]);
      if (process.env.NODE_ENV === 'development') {
        console.error('Load data error:', err);
        console.error('Error response:', err.response);
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [loadDataFunction]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);


  const defaultColumns = [
    { key: 'projectCode', label: 'Project Code' },
    { key: 'budgetCode', label: 'Budget Code' },
    { key: 'requestorName', label: 'Requestor Name' },
    { key: 'personInCharge', label: 'Person In Charge' },
    { key: 'modelName', label: 'Model Name' },
    { key: 'projectStatus', label: 'Project Status' }
  ];

  const displayColumns = columns.length > 0 ? columns : defaultColumns;

  const handleSearch = async (searchCriteria) => {
    setLoading(true);
    setError(null);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    
    try {
      const searchParams = { ...searchCriteria, page: 1, limit: paginationModel.pageSize };
      const response = await searchProjects(searchParams);
      const results = Array.isArray(response) ? response : (response?.results || response?.data || []);
      const formattedData = results.map((item, index) => ({
        id: item.id || item.projectCode || item.project_code || index,
        projectCode: item.projectCode || item.project_code,
        budgetCode: item.budgetCode || item.budget_code,
        requestorName: item.requestorName || item.requestor_name,
        personInCharge: item.personInCharge || item.person_in_charge,
        modelName: item.modelName || item.model_name,
        projectStatus: item.projectStatus || item.project_status,
        backgroundColor: item.backgroundColor
      }));
      setFilteredRows(formattedData);
    } catch (err) {
      setError('Failed to perform search. Please try again.');
      if (process.env.NODE_ENV === 'development') {
        console.error('Search error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setError(null);
    loadInitialData();
  };

  const paginatedRows = filteredRows.slice(
    paginationModel.page * paginationModel.pageSize,
    paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
  );

  return (
    <Box sx={{ p: 3 }}>
      {title && (
        <Typography variant="h4" gutterBottom component="h1">
          {title}
        </Typography>
      )}

      {loading && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      <SearchComponent
        onSearch={handleSearch}
        onReset={handleReset}
        title="Search Criteria"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && filteredRows && Array.isArray(filteredRows) && (
        <Paper sx={{ width: '100%', mb: 2, boxShadow: 2, borderRadius: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-label={`${title || 'Search'} results table`}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  {displayColumns.map((column) => (
                    <TableCell component="th" scope="col" key={column.key || column.field} sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, index) => (
                  <TableRow 
                    key={row.id || row.projectCode || index}
                    hover
                    sx={{
                      backgroundColor: row.backgroundColor || (index % 2 === 0 ? '#fafafa' : 'white'),
                      '&:hover': { 
                        backgroundColor: row.backgroundColor ? '#c4a8a8 !important' : '#e3f2fd !important'
                      }
                    }}
                  >
                    {displayColumns.map((column) => (
                      <TableCell key={column.key || column.field} sx={{ fontSize: '13px', padding: '6px 12px' }}>
                        {column.renderCell ? (
                          column.renderCell(row)
                        ) : (column.key === 'projectCode' || column.field === 'projectCode') && row[column.key || column.field] && getRowLink ? (
                          <Link
                            component={RouterLink}
                            to={getRowLink(row)}
                            aria-label={`View details for ${row[column.key || column.field]}`}
                            sx={{ 
                              color: '#0d47a1',
                              textDecorationThickness: '2px',
                              fontSize: '13px',
                              '&:hover': { color: '#0a3d91' },
                              '&:focus-visible': {
                                outline: '3px solid #000000',
                                outlineOffset: '2px',
                                borderRadius: '2px',
                              }
                            }}
                          >
                            {row[column.key || column.field]}
                          </Link>
                        ) : (
                          row[column.key || column.field] || ''
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={paginationModel.pageSize}
            page={paginationModel.page}
            onPageChange={(event, newPage) => setPaginationModel({ ...paginationModel, page: newPage })}
            onRowsPerPageChange={(event) => setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) })}
            sx={{
              backgroundColor: '#f8f9fa',
              borderTop: '2px solid #e0e0e0',
              '& .MuiTablePagination-root': { color: '#666' }
            }}
          />
        </Paper>
      )}
      
      {loading && (
        <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      )}

      {initialized && !loading && Array.isArray(filteredRows) && filteredRows.length === 0 && !error && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No results found. Please adjust your search criteria.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

UnifiedSearchPage.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    field: PropTypes.string,
    label: PropTypes.string.isRequired,
    renderCell: PropTypes.func
  })),
  onRowClick: PropTypes.func,
  getRowLink: PropTypes.func,
  loadDataFunction: PropTypes.func
};

export default UnifiedSearchPage;